// parallax.ts
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  observeDynamicTabs,
  scrollListener,
  getCurrentBreakpoint,
  setPositionTitleMobile,
  isFullscreen,
  onFullscreenChange,
  isElementFullscreen,
  getPageScrollY,
  handleArrowKeySnap,
  setParallaxAutoSnapSuppressed,
  getParallaxRestScrollY,
} from './parallaxUtils';
import { isAEMAuthorMode, subscribeToResize } from '@msc-b2c/shared-utils';

interface Dims {
  centerW: number;
  centerH: number;
  sideW: number;
  sideH: number;
  edgeGap: number;
  sideZ: number;
  bottom: number;
}

interface BreakpointDims {
  dims: Dims;
  startDims: Dims;
  midDims: Dims;
  endDims: Dims;
}
const TITLE_CARD_GAP_PX = 20;
const scrollPositionTrigger = 50;
const headerHeight = 117;
let CONTENT_CENTER_VH_FRACTION = 0.78;
if (window.innerHeight < 810) {
  CONTENT_CENTER_VH_FRACTION = 0.63;
} else if (window.innerHeight < 900) {
  CONTENT_CENTER_VH_FRACTION = 0.68;
} else if (window.innerHeight < 1100) {
  CONTENT_CENTER_VH_FRACTION = 0.72;
} else if (window.innerHeight < 1280) {
  CONTENT_CENTER_VH_FRACTION = 0.75;
}

const SINGLE_CENTER_VH_FRACTION = 0.65;
const SINGLE_FINAL_VH_FRACTION = 0.5;
const PARALLAX_MEDIA_ASPECT_RATIO = 16 / 9;
const SINGLE_ASPECT_RATIO = PARALLAX_MEDIA_ASPECT_RATIO;
const MOBILE_CARD_WIDTH_LEFT_ALLOWANCE_PX = 16;
const MOBILE_CARD_WIDTH_RIGHT_ALLOWANCE_PX = 56;
const MOBILE_CARD_TOTAL_HORIZONTAL_GAP_PX =
  MOBILE_CARD_WIDTH_LEFT_ALLOWANCE_PX + MOBILE_CARD_WIDTH_RIGHT_ALLOWANCE_PX;
const MOBILE_ACTIVE_CARD_LEFT_PX = 16;
const MOBILE_NEXT_CARD_GAP_PX = 8;
const MOBILE_ACTIONS_HEIGHT_PX = 44;
const MOBILE_GALLERY_BUFFER_PX = 4;

function getMobileCardSize(vw = window.innerWidth, vh = window.innerHeight) {
  if (vw > vh) {
    const maxWidth = vw * 0.4;
    const maxHeight = vh * 0.48;
    const width = Math.min(maxWidth, maxHeight * PARALLAX_MEDIA_ASPECT_RATIO);
    return { width, height: width / PARALLAX_MEDIA_ASPECT_RATIO, useCenteredLayout: true };
  }

  const width = Math.max(0, vw - MOBILE_CARD_TOTAL_HORIZONTAL_GAP_PX);
  return { width, height: width / PARALLAX_MEDIA_ASPECT_RATIO, useCenteredLayout: false };
}

function calculateXXLStartDimBottom() {
  const points = [
    [1300, -60],
    [1200, -50],
    [1100, -20],
    [1000, 20],
    [900, 70],
    [800, 125],
    [700, 170],
    [600, 210],
  ];

  const x = window.innerHeight;

  if (x >= points[0][0]) {
    return points[0][1];
  }

  if (x <= points[points.length - 1][0]) {
    return points[points.length - 1][1];
  }

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];

    if (x <= x1 && x >= x2) {
      const t = (x - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }

  return 0;
}

function calculateXLStartDimBottom() {
  const points = [
    [1400, -118],
    [1300, -118],
    [1200, -118],
    [1100, -87],
    [1000, -38],
    [900, 8],
    [800, 59],
    [700, 105],
    [600, 155],
  ];

  const x = window.innerHeight;

  if (x >= points[0][0]) {
    return points[0][1];
  }

  if (x <= points[points.length - 1][0]) {
    return points[points.length - 1][1];
  }

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];

    if (x <= x1 && x >= x2) {
      const t = (x - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }

  return 0;
}

function calculateLGStartDimBottom() {
  const points = [
    [1400, -168],
    [1300, -160],
    [1200, -162],
    [1100, -195],
    [1080, -135],
    [900, -50],
    [930, -65],
    [950, -70],
    [970, -75],
    [990, -80],
    [800, 0],
    [700, 47],
    [600, 90],
  ];

  points.sort((a, b) => b[0] - a[0]);
  const x = window.innerHeight;

  if (x >= points[0][0]) return points[0][1];

  if (x <= points[points.length - 1][0]) return points[points.length - 1][1];

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];

    if (x <= x1 && x >= x2) {
      const t = (x - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }

  return 0;
}

function calculateMDStartDimBottom() {
  const points = [
    [1200, -100],
    [1100, -90],
    [1000, -20],
    [900, 20],
    [800, 40],
    [700, 60],
    [600, 80],
  ];

  const x = window.innerHeight;

  if (x >= points[0][0]) {
    return points[0][1];
  }

  if (x <= points[points.length - 1][0]) {
    return points[points.length - 1][1];
  }

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];

    if (x <= x1 && x >= x2) {
      const t = (x - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }

  return 40;
}

function calculateSingleItemLGStartPosition() {
  const points = [
    [1500, 0.88],
    [1500, 0.85],
    [1500, 0.83],
    [1300, 0.82],
    [1200, 0.8],
    [1100, 0.78],
    [1024, 0.755],
    [900, 0.73],
    [800, 0.7],
    [700, 0.65],
    [600, 0.6],
  ];

  const vh = window.innerHeight;

  if (vh >= points[0][0]) return points[0][1];
  if (vh <= points[points.length - 1][0]) return points[points.length - 1][1];

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    if (vh <= x1 && vh >= x2) {
      const t = (vh - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }
  return 0.72;
}

function calculateSingleItemXLStartPosition() {
  const points = [
    [2500, 0.88],
    [2000, 0.86],
    [1800, 0.85],
    [1600, 0.84],
    [1600, 0.83],
    [1500, 0.82],
    [1300, 0.81],
    [1300, 0.8],
    [1200, 0.78],
    [1100, 0.76],
    [1080, 0.75],
    [1050, 0.745],
    [1000, 0.74],
    [900, 0.7],
    [800, 0.68],
    [700, 0.62],
    [600, 0.57],
  ];

  const vh = window.innerHeight;

  if (vh >= points[0][0]) return points[0][1];
  if (vh <= points[points.length - 1][0]) return points[points.length - 1][1];

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    if (vh <= x1 && vh >= x2) {
      const t = (vh - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }
  return 0.72;
}

function calculateSingleItemXXLStartPosition() {
  const points = [
    [3000, 0.9],
    [2700, 0.89],
    [2300, 0.88],
    [2200, 0.87],
    [2160, 0.872],
    [2100, 0.86],
    [2000, 0.86],
    [1900, 0.84],
    [1800, 0.83],
    [1700, 0.82],
    [1440, 0.805],
    [1400, 0.8],
    [1300, 0.78],
    [1200, 0.76],
    [1100, 0.74],
    [1000, 0.72],
    [900, 0.68],
    [800, 0.65],
    [700, 0.58],
    [600, 0.54],
  ];

  const vh = window.innerHeight;

  if (vh >= points[0][0]) return points[0][1];
  if (vh <= points[points.length - 1][0]) return points[points.length - 1][1];

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    if (vh <= x1 && vh >= x2) {
      const t = (vh - x2) / (x1 - x2);
      return y2 + t * (y1 - y2);
    }
  }
  return 0.72;
}

function getSingleItemStartPosition() {
  const bp = getCurrentBreakpoint();

  switch (bp) {
    case 'lg':
      return calculateSingleItemLGStartPosition();
    case 'xl':
      return calculateSingleItemXLStartPosition();
    case 'xxl':
      return calculateSingleItemXXLStartPosition();
    default:
      return CONTENT_CENTER_VH_FRACTION;
  }
}

const dimensionsByBreakpoint: Record<string, BreakpointDims> = {
  sm: {
    dims: { centerW: 180, centerH: 112, sideW: 180, sideH: 112, edgeGap: 16, sideZ: 0, bottom: 40 },
    startDims: {
      centerW: 240,
      centerH: 180,
      sideW: 240,
      sideH: 180,
      edgeGap: 16,
      sideZ: 0,
      bottom: 30,
    },
    midDims: {
      centerW: 180,
      centerH: 112,
      sideW: 180,
      sideH: 112,
      edgeGap: 16,
      sideZ: 0,
      bottom: 80,
    },
    endDims: {
      centerW: 180,
      centerH: 112,
      sideW: 180,
      sideH: 112,
      edgeGap: 16,
      sideZ: 0,
      bottom: 80,
    },
  },
  md: {
    dims: {
      centerW: 340,
      centerH: 225,
      sideW: 245,
      sideH: 140,
      edgeGap: -80,
      sideZ: -20,
      bottom: 90,
    },
    startDims: {
      centerW: 340,
      centerH: 225,
      sideW: 240,
      sideH: 135,
      edgeGap: -80,
      sideZ: -20,
      bottom: calculateMDStartDimBottom(),
    },
    midDims: {
      centerW: 510,
      centerH: 305,
      sideW: 255,
      sideH: 145,
      edgeGap: 0,
      sideZ: -20,
      bottom: 90,
    },
    endDims: {
      centerW: 680,
      centerH: 384,
      sideW: 270,
      sideH: 155,
      edgeGap: 16,
      sideZ: -20,
      bottom: 90,
    },
  },
  lg: {
    dims: {
      centerW: 243,
      centerH: 151.875,
      sideW: 167,
      sideH: 93,
      edgeGap: -16,
      sideZ: -20,
      bottom: 100,
    },
    startDims: {
      centerW: 243,
      centerH: 151.875,
      sideW: 167,
      sideH: 93,
      edgeGap: -16,
      sideZ: -20,
      bottom: calculateLGStartDimBottom() - 15,
    },
    midDims: {
      centerW: 326,
      centerH: 191.875,
      sideW: 167,
      sideH: 93,
      edgeGap: 0,
      sideZ: -20,
      bottom: 100,
    },
    endDims: {
      centerW: 425,
      centerH: 240,
      sideW: 167,
      sideH: 93,
      edgeGap: 16,
      sideZ: -20,
      bottom: 100,
    },
  },
  xl: {
    dims: {
      centerW: 296,
      centerH: 185,
      sideW: 200,
      sideH: 113,
      edgeGap: -12,
      sideZ: -20,
      bottom: 110,
    },
    startDims: {
      centerW: 296,
      centerH: 185,
      sideW: 200,
      sideH: 113,
      edgeGap: -12,
      sideZ: -20,
      bottom: calculateXLStartDimBottom() - 10,
    },
    midDims: {
      centerW: 418,
      centerH: 245,
      sideW: 200,
      sideH: 113,
      edgeGap: 4,
      sideZ: -20,
      bottom: 110,
    },
    endDims: {
      centerW: 540,
      centerH: 305,
      sideW: 200,
      sideH: 113,
      edgeGap: 20,
      sideZ: -20,
      bottom: 110,
    },
  },
  xxl: {
    dims: {
      centerW: 400,
      centerH: 225,
      sideW: 239,
      sideH: 135,
      edgeGap: -16,
      sideZ: -20,
      bottom: 110,
    },
    startDims: {
      centerW: 400,
      centerH: 225,
      sideW: 239,
      sideH: 135,
      edgeGap: -16,
      sideZ: -20,
      bottom: calculateXXLStartDimBottom() - 10,
    },
    midDims: {
      centerW: 540,
      centerH: 300,
      sideW: 142,
      sideH: 137,
      edgeGap: 0,
      sideZ: -20,
      bottom: 110,
    },
    endDims: {
      centerW: 680,
      centerH: 384,
      sideW: 245,
      sideH: 140,
      edgeGap: 32,
      sideZ: -20,
      bottom: 110,
    },
  },
};

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function cardsContainerHeight() {
  const vh = window.innerHeight;
  const vw = window.innerWidth;

  if (vw < 1000) {
    const { height } = getMobileCardSize(vw, vh);
    return Math.ceil(height + MOBILE_ACTIONS_HEIGHT_PX + MOBILE_GALLERY_BUFFER_PX);
  }

  return Math.min(Math.max(0.48 * vh, 220), 560);
}

function bottomForCenter() {
  const vh = window.innerHeight;
  const ch = cardsContainerHeight();
  const targetCenterY = vh * CONTENT_CENTER_VH_FRACTION;
  return vh - ch / 2 - targetCenterY;
}

function getCurrentDims(): BreakpointDims {
  const bp = getCurrentBreakpoint();
  return dimensionsByBreakpoint[bp];
}

const isAnimationEnabled = () => window.innerWidth > 1000;

const MAIN_ABOVE_GAP_PX = 12;
const HEADER_OFFSET_PX = 115;
const LOCK_OFFSET_VH = 5;
const intervalDistanceOffset = 50;
const intervalDuration = 50;
const PARALLAX_BOOT_MAX_WAIT_MS = 1200;
const PARALLAX_REFRESH_SETTLE_MS = 220;

const getLockStartPx = () =>
  Math.round(HEADER_OFFSET_PX + window.innerHeight * (LOCK_OFFSET_VH / 100));

function getScrollElement(): HTMLElement {
  return (document.scrollingElement || document.documentElement) as HTMLElement;
}
function lockScroll(lock: boolean) {
  document.documentElement.classList.toggle('is-parallax-booting', lock);
  document.body.classList.toggle('is-parallax-booting', lock);
}

let refreshRafId = 0;
function scheduleScrollTriggerRefresh() {
  if (refreshRafId) return;
  refreshRafId = requestAnimationFrame(() => {
    refreshRafId = 0;
    try {
      ScrollTrigger.refresh();
    } catch {
      // best-effort cleanup; safe to ignore failures here
    }
  });
}

function withTimeout(p: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    p.finally(() => {
      window.clearTimeout(timer);
      done();
    });
  });
}

function getParallaxLayoutSignature(): string {
  const root = document.querySelector<HTMLElement>('.cmp-parallax');
  if (!root) return '';

  const bg = root.querySelector<HTMLElement>('.cmp-parallax__background');
  const cards = root.querySelector<HTMLElement>('.cmp-parallax-carousel .cards');
  const singleMedia = root.querySelector<HTMLElement>(
    '.cmp-single-item .imagevideo, .cmp-single-item .cmp-media'
  );
  const rootRect = root.getBoundingClientRect();
  const singleRect = singleMedia?.getBoundingClientRect();

  return [
    Math.round(rootRect.height),
    bg?.offsetHeight ?? -1,
    cards?.offsetHeight ?? -1,
    Math.round(singleRect?.height || -1),
    window.innerWidth,
    window.innerHeight,
  ].join('|');
}
function whenImageReady(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  if (typeof img.decode === 'function') return img.decode().catch(() => void 0);
  return new Promise((res) => {
    img.addEventListener('load', () => res(), { once: true });
    img.addEventListener('error', () => res(), { once: true });
  });
}

function safePlay(v: HTMLVideoElement) {
  try {
    const p = v.play();
    if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
  } catch {
    console.error('Failed to play parallax video');
  }
}

function safePause(v: HTMLVideoElement) {
  try {
    v.pause();
  } catch {
    console.error('Failed to pause parallax video');
  }
}

function shouldResumeParallaxVideo(v: HTMLVideoElement): boolean {
  if (isFS || isFullscreen() || isElementFullscreen(v)) return false;
  if (!v.isConnected) return false;

  const single = v.closest<HTMLElement>('.cmp-single-item');
  if (single) {
    // Single-item video is only intended to play after the scroll activation point.
    return single.classList.contains('activate-video');
  }

  const card = v.closest<HTMLElement>('.cmp-parallax-carousel .cards li');
  if (card) {
    // Carousel videos are intended to play only on the active (center/left on mobile) card.
    return card.classList.contains('is-center') || card.classList.contains('is-left');
  }

  // Fallback for any other parallax video.
  return v.closest('.cmp-parallax') !== null;
}

function installParallaxVideoInViewBehavior(scope: Element) {
  const VISIBILITY_THRESHOLD = 0.25;

  const observed = new WeakSet<HTMLVideoElement>();
  const inView = new WeakMap<HTMLVideoElement, boolean>();

  const io =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              const v = e.target as HTMLVideoElement;
              const visible = e.isIntersecting && e.intersectionRatio >= VISIBILITY_THRESHOLD;
              inView.set(v, visible);

              if (!visible) {
                // If it’s out of view, always stop playback
                safePause(v);
                continue;
              }

              // If it’s in view, only resume if parallax logic expects it to be playing.
              if (shouldResumeParallaxVideo(v)) {
                safePlay(v);
              }
            }
          },
          { threshold: [0, VISIBILITY_THRESHOLD] }
        )
      : null;

  const observeVideo = (v: HTMLVideoElement) => {
    if (observed.has(v)) return;
    observed.add(v);

    if (io) {
      io.observe(v);
      return;
    }

    // Fallback: if IO is unavailable, leave existing behavior unchanged.
    inView.set(v, true);
  };

  const observeAll = () => {
    scope.querySelectorAll<HTMLVideoElement>('video').forEach((v) => observeVideo(v));
  };

  observeAll();

  const mo = new MutationObserver(() => observeAll());
  mo.observe(scope, { childList: true, subtree: true });

  return () => {
    try {
      mo.disconnect();
      io?.disconnect();
    } catch {
      // best-effort cleanup; safe to ignore failures here
    }
  };
}
async function waitForCriticalParallaxAssets(maxWaitMs = PARALLAX_BOOT_MAX_WAIT_MS): Promise<void> {
  const root = document.querySelector('.cmp-parallax') || document;
  const isDesktop = isAnimationEnabled();
  const criticalImgs: HTMLImageElement[] = [];
  const bg = root.querySelector<HTMLImageElement>('.cmp-parallax__background img');
  if (bg) criticalImgs.push(bg);

  root
    .querySelectorAll<HTMLImageElement>('.cmp-parallax-carousel .cards li:nth-child(-n+2) img')
    .forEach((img) => criticalImgs.push(img));

  if (isDesktop) {
    const singleDesktopImg = root.querySelector<HTMLImageElement>(
      '.cmp-single-item .imagevideo img'
    );
    if (singleDesktopImg) criticalImgs.push(singleDesktopImg);
  } else {
    root
      .querySelectorAll<HTMLImageElement>('.cmp-single-item .imagevideo img')
      .forEach((img) => criticalImgs.push(img));
  }

  const vids = Array.from(root.querySelectorAll<HTMLVideoElement>('.cmp-parallax video'));
  vids.forEach((video) => {
    if (video.readyState >= 2) return;
    video.addEventListener('loadeddata', scheduleScrollTriggerRefresh, { once: true });
  });

  const fontsReady = (document as any).fonts?.ready;
  if (fontsReady && typeof fontsReady.then === 'function') {
    (fontsReady as Promise<any>).then(() => scheduleScrollTriggerRefresh()).catch(() => void 0);
  }

  await withTimeout(
    Promise.all(criticalImgs.map(whenImageReady)).then(() => void 0),
    maxWaitMs
  );
}

function installMediaObserver(scope: Element) {
  let raf = 0;
  const observer = new MutationObserver((mutations) => {
    const needs = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) =>
          n.nodeType === 1 &&
          ((n as HTMLElement).tagName === 'IMG' || (n as HTMLElement).tagName === 'VIDEO')
      )
    );
    if (!needs) return;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      observer.disconnect();
      scheduleScrollTriggerRefresh();
      observer.observe(scope, { childList: true, subtree: true });
    });
  });
  observer.observe(scope, { childList: true, subtree: true });
}

function installScrollActivityClass(el: HTMLElement, className: string, idleMs = 500) {
  let on = false;
  let timer: number | null = null;
  const setOn = (v: boolean) => {
    if (on === v) return;
    on = v;
    el.classList.toggle(className, v);
  };
  const rectContains = (x: number, y: number) => {
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };
  const signalFromPoint = (x: number, y: number) => {
    if (isFS || isFullscreen()) return;
    if (!rectContains(x, y)) return;
    setOn(true);
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => setOn(false), idleMs);
  };

  const onWheel = (e: WheelEvent) => signalFromPoint(e.clientX, e.clientY);
  const onTouchMove = (e: TouchEvent) => {
    const t = e.touches[0] || e.changedTouches[0];
    if (t) signalFromPoint(t.clientX, t.clientY);
  };
  document.addEventListener('wheel', onWheel, { passive: true, capture: true });
  document.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
  return () => {
    document.removeEventListener('wheel', onWheel, { capture: true } as any);
    document.removeEventListener('touchmove', onTouchMove, { capture: true } as any);
  };
}

function initTopCtaButton(): void {
  const parallaxRoot = document.querySelector<HTMLElement>('.cmp-parallax');
  if (!parallaxRoot) return;

  const wrapper = parallaxRoot.querySelector<HTMLElement>('.cmp-parallax__button');
  if (!wrapper) return;

  const btn =
    wrapper.querySelector<HTMLButtonElement>('button') ||
    wrapper.querySelector<HTMLButtonElement>('.cmp-button');
  if (!btn) return;

  const updateVisibility = () => {
    const scrollTop = getPageScrollY();
    const shouldShow = scrollTop <= 5;
    wrapper.classList.toggle('is-visible', shouldShow);
  };

  updateVisibility();

  const onScroll = () => {
    updateVisibility();
  };

  document.body.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });

  const cleanupTopCtaResize = subscribeToResize(() => {
    if (!parallaxRoot.isConnected) {
      cleanupTopCtaResize();
      return;
    }
    updateVisibility();
  });

  const scrollDown = () => {
    if (isFullscreen()) return;

    const sts = ScrollTrigger.getAll();

    const matchingSTs = sts.filter(
      (st) =>
        st.trigger?.classList.contains('cmp-parallax-carousel') ||
        st.trigger?.classList.contains('cmp-single-item')
    );

    if (matchingSTs.length === 0) return;

    const mainST = matchingSTs.reduce((max, st) => (st.end > max.end ? st : max));

    const viewportPercentage = 0.3;
    const minOffset = 250;

    const targetTopOffset = Math.max(window.innerHeight * viewportPercentage, minOffset);

    const targetTop = mainST.end - targetTopOffset;
    const startTop = document.body.scrollTop;
    const distance = targetTop - startTop;

    if (distance <= 0) return;

    const duration = 1000;
    const startTime = performance.now();

    const easeInOut = (t: number) => {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOut(progress);

      const newScroll = Math.round(startTop + distance * easedProgress);
      document.body.scrollTop = newScroll;
      ScrollTrigger.update();

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const html = document.documentElement;
    html.style.setProperty('scroll-behavior', 'auto', 'important');
    document.body.style.setProperty('scroll-behavior', 'auto', 'important');

    requestAnimationFrame(animate);
  };

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    scrollDown();
  });
}

const endPosition = Math.round(window.innerHeight) - 110;

let isFS = false;
const DRAGGABLES: any[] = [];
let lastFSExitAt = 0;
const RECENT_EXIT_MS = 1500;
let lastFSEnterAt = 0;
const RECENT_ENTER_MS = 1200;
function setFullscreenMode(on: boolean) {
  isFS = on;

  document.documentElement.classList.toggle('is-video-fullscreen', on);

  if (on) {
    document
      .querySelectorAll('.gallery-is-scrolling, .single-item-is-scrolling')
      .forEach((el) => el.classList.remove('gallery-is-scrolling', 'single-item-is-scrolling'));
  }

  try {
    ScrollTrigger.getAll().forEach((st) => {
      on ? st.disable(false) : st.enable(false);
    });
  } catch {
    /* empty */
  }
  if (!on) {
    lastFSExitAt = Date.now();
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }
}
onFullscreenChange(setFullscreenMode);

function ensurePlayingUnmuted(v: HTMLVideoElement) {
  try {
    v.muted = false;
    v.removeAttribute('muted');
    v.volume = Math.max(v.volume || 0, 0.8);
    const p = v.play();
    if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
  } catch {
    /* empty */
  }
}
function recentlyExitedFS() {
  return Date.now() - lastFSExitAt < RECENT_EXIT_MS;
}
function recentlyEnteredFS() {
  return Date.now() - lastFSEnterAt < RECENT_ENTER_MS;
}

/** ------------------------------------------------------------------
 *  MOBILE TITLE WRAP DETECTION (adds/removes .is-wrapped on
 *  .cmp-parallax__title--mobile to let CSS switch top: 43svh/38svh)
 *  ------------------------------------------------------------------ */
function getMobileTitleContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.cmp-parallax__title--mobile');
}

function getMobileTitleTextEl(container: HTMLElement): HTMLElement {
  const el =
    container.querySelector<HTMLElement>(
      '.cmp-title, .cmp-title__text, p, span, [data-title], :scope > *'
    ) || container;
  return el;
}

function linesWrapped(el: HTMLElement): boolean {
  // Prefer multiple client rects (1 per visual line for inline content)
  if (el.getClientRects().length > 1) return true;

  // Fallback: overflow in inline axis
  if (el.scrollWidth > el.clientWidth) return true;

  // Fallback: estimate by height / line-height
  const cs = window.getComputedStyle(el);
  const lhRaw = cs.lineHeight;
  let lineHeightPx: number;
  if (lhRaw.endsWith?.('px')) {
    lineHeightPx = parseFloat(lhRaw);
  } else {
    const fs = parseFloat(cs.fontSize || '16');
    const unitless = parseFloat(lhRaw);
    lineHeightPx = Number.isFinite(unitless) && unitless > 0 ? unitless * fs : fs * 1.2;
  }
  const rect = el.getBoundingClientRect();
  const approxLines = Math.round(rect.height / Math.max(lineHeightPx, 1));
  return approxLines > 1;
}

export function updateMobileTitleWrapState() {
  const container = getMobileTitleContainer();
  if (!container) return;
  const textEl = getMobileTitleTextEl(container);
  const wrapped = linesWrapped(textEl);
  container.classList.toggle('is-wrapped', wrapped);
}

function observeMobileTitleForChanges() {
  const container = getMobileTitleContainer();
  if (!container) return;
  const textEl = getMobileTitleTextEl(container);

  const ro = new ResizeObserver(() => updateMobileTitleWrapState());
  ro.observe(textEl);

  const cleanupMobileTitleResize = subscribeToResize(() => {
    if (!container.isConnected) {
      cleanupMobileTitleResize();
      return;
    }
    updateMobileTitleWrapState();
  });

  try {
    ScrollTrigger.addEventListener('refresh', updateMobileTitleWrapState);
  } catch {
    // ScrollTrigger not ready yet; fine.
  }
}
/** ------------------------------------------------------------------ */

function initParallaxSingleItem(): void {
  gsap.registerPlugin(ScrollTrigger);

  const singleItems = Array.from(document.querySelectorAll<HTMLElement>('.cmp-single-item'));

  singleItems.forEach((root) => {
    const parallaxContainer = root.closest('.cmp-parallax');
    if (!parallaxContainer) return;

    const isStatic = parallaxContainer.classList.contains('-static');
    const itemContent = root.querySelector<HTMLElement>('.imagevideo, .cmp-media');
    if (!itemContent) return;

    const singleVideo = root.querySelector<HTMLVideoElement>('video');

    if (singleVideo) {
      (singleVideo as any).addEventListener?.('webkitbeginfullscreen', () => {
        setFullscreenMode(true);
        lastFSEnterAt = Date.now();
        ensurePlayingUnmuted(singleVideo);
        requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
        setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
      });
      (singleVideo as any).addEventListener?.('webkitendfullscreen', () => {
        setFullscreenMode(false);
        lastFSExitAt = Date.now();
        ensurePlayingUnmuted(singleVideo);
        setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
        setTimeout(() => ensurePlayingUnmuted(singleVideo), 300);
        requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
      });

      singleVideo.addEventListener(
        'pause',
        () => {
          if (recentlyEnteredFS() || recentlyExitedFS()) {
            ensurePlayingUnmuted(singleVideo);
          }
        },
        true
      );
      singleVideo.addEventListener('volumechange', () => {
        if ((recentlyEnteredFS() || recentlyExitedFS()) && singleVideo.muted) {
          ensurePlayingUnmuted(singleVideo);
        }
      });

      document.addEventListener('fullscreenchange', () => {
        if (isElementFullscreen(singleVideo) || isFullscreen()) {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
        } else {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
        }
      });
      (document as any).addEventListener('webkitfullscreenchange', () => {
        if (isElementFullscreen(singleVideo) || isFullscreen()) {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
        } else {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
        }
      });
      (singleVideo as any).addEventListener?.('webkitpresentationmodechanged', () => {
        if ((singleVideo as any).webkitPresentationMode === 'fullscreen') {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
        }
        if ((singleVideo as any).webkitPresentationMode === 'inline') {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(singleVideo);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 120);
          setTimeout(() => ensurePlayingUnmuted(singleVideo), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(singleVideo));
        }
      });
    }

    function computeSingleItemEndDims() {
      const vh = window.innerHeight;
      const finalH = Math.round(vh * SINGLE_FINAL_VH_FRACTION); // 50vh
      const finalW = Math.round(finalH * SINGLE_ASPECT_RATIO); // 16:9
      return { centerW: finalW, centerH: finalH };
    }

    const currentDims = getCurrentDims();

    const startDims = {
      centerW: currentDims.startDims.centerW,
      centerH: currentDims.startDims.centerH,
    };
    const midDims = {
      centerW: currentDims.midDims.centerW,
      centerH: currentDims.midDims.centerH,
    };
    let endDims = computeSingleItemEndDims();

    const state = {
      dims: { ...startDims },
      startDims: { ...startDims },
      midDims: { ...midDims },
      endDims: { ...endDims },
    };

    gsap.set(root, {
      css: {
        '--single-w': state.startDims.centerW,
        '--single-h': state.startDims.centerH,
        position: 'absolute',
        width: '100vw',
        height: '100vh',
        top: 0,
        left: 0,
        zIndex: 1,
      },
    });

    gsap.set(itemContent, {
      position: 'absolute',
      left: '50%',
      top: `${getSingleItemStartPosition() * 100}%`,
      xPercent: -50,
      yPercent: -50,
      width: `${state.startDims.centerW}px`,
      height: `${state.startDims.centerH}px`,
      willChange: 'transform, width, height',
      transform: 'translate3d(0,0,0)',
    });

    const titleEl = parallaxContainer.querySelector<HTMLElement>('.cmp-parallax__title .cmp-title');
    const paraMainEl = parallaxContainer.querySelector<HTMLElement>(
      '.cmp-parallax__paragraph-main'
    );
    const paraSecondaryEl = parallaxContainer.querySelector<HTMLElement>(
      '.cmp-parallax__paragraph-secondary'
    );
    const bgEl = parallaxContainer.querySelector<HTMLElement>('.cmp-parallax__background');

    let titleWarmupST: ScrollTrigger | null = null;
    let titleMainST: ScrollTrigger | null = null;

    function buildSingleItemTitleAnimations(getPinnedRange: () => number) {
      if (!titleEl || !isAnimationEnabled()) return;

      if (titleWarmupST) {
        titleWarmupST.kill();
        titleWarmupST = null;
      }
      if (titleMainST) {
        titleMainST.kill();
        titleMainST = null;
      }

      const totalScrollRange = getPinnedRange();
      const TITLE_SPEED = 1.2;
      const totalMove = totalScrollRange * TITLE_SPEED;

      let savedYStart: number | null = null;

      const createAnimation = () => {
        if (savedYStart === null) {
          savedYStart = (gsap.getProperty(titleEl, 'y') as number) || 0;
        }

        const yStart = savedYStart;
        const yEnd = yStart - totalMove;

        gsap.set(titleEl, {
          willChange: 'transform, opacity',
          force3D: true,
        });

        const tl = gsap.timeline({ paused: true });

        tl.fromTo(
          titleEl,
          { y: yStart, opacity: 1 },
          {
            y: yEnd,
            opacity: 1,
            ease: 'power3.out',
            duration: 1,
          }
        );

        tl.to(
          titleEl,
          {
            opacity: 0,
            ease: 'none',
            duration: 0.4,
          },
          0.3
        );

        titleWarmupST = ScrollTrigger.create({
          trigger: root,
          start: () => `top-=${HEADER_OFFSET_PX} top`,
          end: () => `+=${totalScrollRange}`,
          scrub: 0.3,
          animation: tl,
          invalidateOnRefresh: true,
          onRefresh: () => {
            if (titleWarmupST && titleWarmupST.progress < 0.01 && savedYStart !== null) {
              gsap.set(titleEl, {
                y: savedYStart,
                opacity: 1,
                xPercent: -50,
              });
            }
          },
        });
      };

      positionTitleAboveCard(createAnimation);
    }

    function positionTitleAboveCard(onComplete?: () => void): void {
      if (!titleEl || !itemContent) return;

      // We need to wait for ScrollTrigger to be refreshed so we know where "start" is
      gsap.delayedCall(0.1, () => {
        const st = ScrollTrigger.getById('singleItemST'); // *See Note below

        // Fallback if ST isn't found (e.g. static mode), use standard rect
        if (!st || !st.start) {
          const cardRect = itemContent.getBoundingClientRect();
          const titleRect = titleEl.getBoundingClientRect();
          const currentY = (gsap.getProperty(titleEl, 'y') as number) || 0;
          const diff = cardRect.top - TITLE_CARD_GAP_PX - (titleRect.bottom - currentY);
          gsap.set(titleEl, { y: currentY + diff, opacity: 1 });
          if (onComplete) onComplete();
          return;
        }

        const startVhFraction = getSingleItemStartPosition();
        const startY_viewport = window.innerHeight * startVhFraction;
        const cardHalfHeight = state.startDims.centerH / 2;
        const cardTop_viewport = startY_viewport - cardHalfHeight;

        // 2. Where is the title currently?
        // We strip out the current transform to get the "natural" position
        const currentY = (gsap.getProperty(titleEl, 'y') as number) || 0;
        const titleRect = titleEl.getBoundingClientRect();
        const titleNaturalTop_viewport = titleRect.top - currentY;
        const titleHeight = titleRect.height;

        const targetY =
          cardTop_viewport - TITLE_CARD_GAP_PX - titleHeight - titleNaturalTop_viewport;

        gsap.set(titleEl, {
          y: targetY,
          xPercent: -50,
          opacity: 1,
          force3D: true,
          willChange: 'transform, opacity',
        });

        if (onComplete) onComplete();
      });
    }

    function applySingleItemDims(dims: { centerW: number; centerH: number }) {
      state.dims = dims;
      root.style.setProperty('--single-w', String(dims.centerW));
      root.style.setProperty('--single-h', String(dims.centerH));
      gsap.set(itemContent, { width: dims.centerW, height: dims.centerH });
    }

    function placeParagraphs() {
      if (!paraMainEl && !paraSecondaryEl) return;

      const vh = window.innerHeight;

      // Final card geometry (50vh tall, centered at SINGLE_CENTER_VH_FRACTION)
      const finalCenterY = vh * SINGLE_CENTER_VH_FRACTION;
      const finalH = state.endDims.centerH || vh * SINGLE_FINAL_VH_FRACTION;
      const halfH = finalH / 2;

      const cardTop = finalCenterY - halfH;
      const cardBottom = finalCenterY + halfH;

      const GAP_PX = 20;

      // Make sure paragraphs are in the DOM under the single-item root so we can measure them
      if (paraMainEl && !root.contains(paraMainEl)) {
        root.appendChild(paraMainEl);
      }
      if (paraSecondaryEl && !root.contains(paraSecondaryEl)) {
        root.appendChild(paraSecondaryEl);
      }

      // Temporarily reset positioning to measure natural heights
      if (paraMainEl) {
        paraMainEl.style.position = '';
        paraMainEl.style.transform = '';
      }
      if (paraSecondaryEl) {
        paraSecondaryEl.style.position = '';
        paraSecondaryEl.style.transform = '';
      }

      const mainH = paraMainEl ? paraMainEl.getBoundingClientRect().height : 0;

      // Position so that:
      // - main paragraph bottom is 20px above card top
      // - secondary paragraph top is 20px below card bottom
      const mainTop = cardTop - GAP_PX - mainH;
      const secondaryTop = cardBottom + GAP_PX;

      if (paraMainEl) {
        gsap.set(paraMainEl, {
          position: 'absolute',
          left: '50%',
          xPercent: -50,
          top: mainTop,
          zIndex: 11,
          pointerEvents: 'none',
          willChange: 'transform, opacity',
          y: 60,
          opacity: 0,
          force3D: true,
        });
      }

      if (paraSecondaryEl) {
        gsap.set(paraSecondaryEl, {
          position: 'absolute',
          left: '50%',
          xPercent: -50,
          top: secondaryTop,
          zIndex: 11,
          pointerEvents: 'none',
          willChange: 'transform, opacity',
          y: 60,
          opacity: 0,
          force3D: true,
        });
      }
    }

    placeParagraphs();

    if (isStatic || isAEMAuthorMode()) return;

    const wheelDelegated: EventListener = () => {};
    parallaxContainer.addEventListener('wheel', wheelDelegated, { capture: true, passive: true });

    const SMOOTH_SCRUB = 0.22;
    const smoothProg = { value: 0 };
    let smoothTween: gsap.core.Tween | null = null;
    function smoothUpdate(target: number) {
      if (smoothTween) smoothTween.kill();
      smoothTween = gsap.to(smoothProg, {
        value: target,
        duration: SMOOTH_SCRUB,
        ease: 'power3.out',
        overwrite: true,
        onUpdate: () => updateByProgress(smoothProg.value),
      });
    }

    if (isAnimationEnabled()) {
      buildSingleItemTitleAnimations(() => {
        const st = ScrollTrigger.getAll().find((st) => st.trigger === root);
        if (!st) return window.innerHeight;
        return st.end - st.start;
      });
    }

    function updateByProgress(p: number) {
      if (isFS || isFullscreen()) return;

      const COMPRESS_END = 0.5;
      const effective = Math.min(1, p / COMPRESS_END);
      const split = 0.56;

      const isExpanded =
        document.querySelector('.cmp-parallax__paragraph-secondary.-expanded-text') !== null;

      if (bgEl && !isExpanded) {
        gsap.set(bgEl, { '--bg-overlay-op': effective });
      }

      if (effective < split) {
        const t = effective / split;
        applySingleItemDims({
          centerW: startDims.centerW + (midDims.centerW - startDims.centerW) * t,
          centerH: startDims.centerH + (midDims.centerH - startDims.centerH) * t,
        });
      } else {
        const t = (effective - split) / (1 - split);
        applySingleItemDims({
          centerW: midDims.centerW + (endDims.centerW - midDims.centerW) * t,
          centerH: midDims.centerH + (endDims.centerH - midDims.centerH) * t,
        });
      }

      const startPosition = getSingleItemStartPosition();
      const centerFraction = lerp(startPosition, SINGLE_CENTER_VH_FRACTION, effective);
      gsap.set(itemContent, { top: `${centerFraction * 100}%` });

      const fadeT = Math.max(0, Math.min(1, (effective - 0.1) / 0.3));
      if (paraMainEl) gsap.set(paraMainEl, { opacity: fadeT * 0.8, y: (1 - fadeT) * 60 });
      if (paraSecondaryEl) gsap.set(paraSecondaryEl, { opacity: fadeT * 0.8, y: (1 - fadeT) * 60 });

      if (titleEl) {
        const titleT = Math.min(1, effective / 0.5);
        titleEl.style.opacity = String(1 - titleT);
      }
    }

    let singleLeaveCenterIntervalId: number | null = null;
    let singleWasPushedDown = false;
    let singleST: ScrollTrigger;

    const syncSingleLeaveCenterState = () => {
      if (!root.isConnected) {
        stopSingleLeaveCenterInterval();
        return;
      }
      if (isFS || isFullscreen()) return;

      const singleEndAbsolute = singleST.end;
      const currentScroll =
        window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      if (currentScroll >= singleEndAbsolute - scrollPositionTrigger + intervalDistanceOffset) {
        if (!singleWasPushedDown) {
          root.classList.add('leave-center');
          singleWasPushedDown = true;
        }
      } else if (singleWasPushedDown) {
        root.classList.remove('leave-center');
        singleWasPushedDown = false;
      }
    };

    const startSingleLeaveCenterInterval = () => {
      if (!isAnimationEnabled()) return;
      if (singleLeaveCenterIntervalId !== null) return;
      singleLeaveCenterIntervalId = window.setInterval(
        syncSingleLeaveCenterState,
        intervalDuration
      );
      syncSingleLeaveCenterState();
    };

    const stopSingleLeaveCenterInterval = () => {
      if (singleLeaveCenterIntervalId === null) return;
      window.clearInterval(singleLeaveCenterIntervalId);
      singleLeaveCenterIntervalId = null;
    };

    singleST = ScrollTrigger.create({
      trigger: root,
      start: `top+=${getLockStartPx()} top`,
      end: 'bottom top+=1',
      pin: isAnimationEnabled(),
      pinType: 'fixed',
      snap: isAnimationEnabled() ? { snapTo: 0.1, duration: 0.1, ease: 'power3.out' } : undefined,
      scrub: isAnimationEnabled(),
      anticipatePin: 0.5,
      fastScrollEnd: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (isFS || isFullscreen()) return;
        const video = root.querySelector<HTMLVideoElement>('video');
        if (!isAnimationEnabled()) return;

        let prog = self.progress;
        const v = (self as any).getVelocity ? (self as any).getVelocity() : 0;

        if (
          Date.now() - lastFSExitAt > RECENT_EXIT_MS &&
          self.direction === -1 &&
          prog <= 0.3 &&
          v < -10
        ) {
          (document.querySelector('.cmp-text__button--less') as HTMLButtonElement)?.click();

          const bgEl = root
            .closest('.cmp-parallax')
            ?.querySelector<HTMLElement>('.cmp-parallax__background');
          if (bgEl) {
            gsap.to(bgEl, {
              '--bg-overlay-op': prog,
              duration: 0.4,
              ease: 'power2.out',
            });
          }
          if (video) {
            requestAnimationFrame(() => {
              if (!isElementFullscreen(video) && !(isFS || isFullscreen())) {
                root.classList.remove('activate-video');
                video.pause();
                video.currentTime = 0;
              }
            });
          }
        }

        if (prog >= 0.5 && self.direction === 1) {
          self.vars.snap = undefined;
          self.update();
          prog = 1;
          root.classList.add('activate-video');
          if (video && !(isFS || isFullscreen())) video.play();
        }

        return smoothUpdate(prog);
      },
      onEnter: () => {
        startSingleLeaveCenterInterval();
      },
      onLeave: () => {
        stopSingleLeaveCenterInterval();
        root?.classList.add('leave-center');
      },
      onEnterBack: () => {
        root.classList.remove('leave-center');
        singleWasPushedDown = false;
        startSingleLeaveCenterInterval();
        requestAnimationFrame(() => root?.classList.remove('leave-center'));
      },
      onLeaveBack: () => {
        stopSingleLeaveCenterInterval();
        root.classList.remove('leave-center');
        singleWasPushedDown = false;
      },
    });

    if (isAnimationEnabled() && singleST.isActive) {
      startSingleLeaveCenterInterval();
    }

    installMediaObserver(root.closest('.cmp-parallax') || root);

    installScrollActivityClass(root, 'single-item-is-scrolling', 500);

    const cleanupSingleItemResize = subscribeToResize(() => {
      if (!root.isConnected) {
        cleanupSingleItemResize();
        return;
      }
      if (!root.isConnected) return;
      if (isFS || isFullscreen()) return;
      endDims = computeSingleItemEndDims();
      state.endDims = { ...endDims };

      applySingleItemDims(startDims);
      placeParagraphs();
      ScrollTrigger.refresh();
    });
  });
}

function initParallaxCarousel(): void {
  gsap.registerPlugin(Draggable, ScrollTrigger);

  const carousels = Array.from(document.querySelectorAll<HTMLElement>('.cmp-parallax-carousel'));

  carousels.forEach((root) => {
    let didStartupAnim = false;

    function runStartupAnimation() {
      if (didStartupAnim) return;
      if (!isAnimationEnabled()) return;
      if (isAEMAuthorMode()) return;
      if (isFS || isFullscreen()) return;
      if (root.closest('.cmp-parallax')?.classList.contains('-static')) return;

      didStartupAnim = true;

      const total = cards.length;
      const EXTRA_SIDE = 200;
      const CENTER_UP = 150;

      const tl = gsap.timeline({ defaults: { duration: 1.5, ease: 'power2.out' } });

      cards.forEach((el, i) => {
        const rel = (((i - state.index) % total) + total) % total;

        tl.from(el, { opacity: 0 }, 0);

        if (window.innerWidth >= 1000) {
          if (rel === 0) {
            tl.from(el, { y: -CENTER_UP }, 0);
          } else if (rel === 1) {
            tl.from(el, { x: `+=${EXTRA_SIDE}` }, 0);
          } else if (rel === total - 1) {
            tl.from(el, { x: `-=${EXTRA_SIDE}` }, 0);
          }
        }
      });
    }
    const cards = Array.from(root.querySelectorAll<HTMLLIElement>('.cards li'));
    if (cards.length <= 1) return;

    const isStatic = root.closest('.cmp-parallax')?.classList.contains('-static');
    const gallery = root.querySelector<HTMLElement>('.gallery');
    const prevBtn = root.querySelector<HTMLButtonElement>('.prev');
    const nextBtn = root.querySelector<HTMLButtonElement>('.next');
    const proxy = root.querySelector<HTMLElement>('.drag-proxy') || root;
    const actions = root.querySelector<HTMLElement>('.actions');

    const wrapIndex = gsap.utils.wrap(0, cards.length);

    const CAROUSEL_SPLIT = 0.56;

    function applyDims(d: Dims) {
      state.dims = d;
      root.style.setProperty('--center-w', String(d.centerW));
      root.style.setProperty('--center-h', String(d.centerH));
      root.style.setProperty('--side-w', String(d.sideW));
      root.style.setProperty('--side-h', String(d.sideH));
      root.style.setProperty('--edge-gap', String(d.edgeGap));
      root.style.setProperty('--side-z', String(d.sideZ));
      root.style.setProperty('--carousel-bottom', String(d.bottom));
    }

    const SMOOTH_SCRUB = 0.22;
    const smoothProg = { value: 0 };
    let smoothTween: gsap.core.Tween | null = null;
    function smoothUpdate(target: number) {
      if (smoothTween) smoothTween.kill();
      smoothTween = gsap.to(smoothProg, {
        value: target,
        duration: SMOOTH_SCRUB,
        ease: 'power3.out',
        overwrite: true,
        onUpdate: () => updateByProgress(smoothProg.value),
      });
    }

    const currentDims = getCurrentDims();

    const state = {
      index: 0,
      dims: { ...currentDims.startDims },
      startDims: { ...currentDims.startDims },
      midDims: { ...currentDims.midDims },
      endDims: { ...currentDims.endDims },
    };

    const isMobileViewport = () => window.innerWidth < 1000;
    const mobileScroll = { position: state.index };
    let mobileSnap: gsap.core.Tween | null = null;

    function mobileTrack() {
      const { width, useCenteredLayout } = getMobileCardSize();
      return {
        baseX: useCenteredLayout
          ? 0
          : -window.innerWidth / 2 + MOBILE_ACTIVE_CARD_LEFT_PX + width / 2,
        step: useCenteredLayout ? width + state.dims.edgeGap : width + MOBILE_NEXT_CARD_GAP_PX,
      };
    }

    function markActiveCard(index: number) {
      const active = wrapIndex(index);
      cards.forEach((el, i) => {
        el.classList.toggle('is-left', i === active);
        el.classList.toggle('is-center', false);
      });
    }

    function drawMobileCards(position: number) {
      const { width, height } = getMobileCardSize();
      const { baseX, step } = mobileTrack();
      const half = cards.length / 2;
      cards.forEach((el, i) => {
        const slot = gsap.utils.wrap(-half, half, i - position);
        gsap.set(el, { x: baseX + slot * step, width, height, opacity: 1, z: 0, zIndex: 2 });
      });
      markActiveCard(Math.round(position));
    }

    function playActiveCardVideo() {
      if (isFS || isFullscreen()) return;
      const active = wrapIndex(state.index);
      cards.forEach((el, i) => {
        const video = el.querySelector<HTMLVideoElement>('video');
        if (!video) return;
        if (i === active) {
          try {
            video.play();
          } catch {
            /* empty */
          }
          video.setAttribute('controls', 'true');
        } else {
          video.pause();
          video.removeAttribute('controls');
        }
      });
    }

    function snapMobileTo(target: number) {
      mobileSnap?.kill();
      const half = cards.length / 2;
      // Always travel the short way around the loop.
      const destination =
        mobileScroll.position + gsap.utils.wrap(-half, half, target - mobileScroll.position);
      mobileSnap = gsap.to(mobileScroll, {
        position: destination,
        duration: 0.35,
        ease: 'power3.out',
        onUpdate: () => drawMobileCards(mobileScroll.position),
        onComplete: () => {
          state.index = wrapIndex(Math.round(destination));
          mobileScroll.position = state.index;
          drawMobileCards(state.index);
          updateDotsActive();
          playActiveCardVideo();
        },
      });
    }

    const getCenterCard = () => cards[wrapIndex(state.index)];
    const getCenterVideo = (): HTMLVideoElement | null =>
      getCenterCard()?.querySelector<HTMLVideoElement>('video') ?? null;

    function attachKeepPlaying(v: HTMLVideoElement) {
      (v as any).addEventListener?.('webkitbeginfullscreen', () => {
        setFullscreenMode(true);
        lastFSEnterAt = Date.now();
        ensurePlayingUnmuted(v);
        requestAnimationFrame(() => ensurePlayingUnmuted(v));
        setTimeout(() => ensurePlayingUnmuted(v), 120);
      });
      (v as any).addEventListener?.('webkitendfullscreen', () => {
        setFullscreenMode(false);
        lastFSExitAt = Date.now();
        ensurePlayingUnmuted(v);
        setTimeout(() => ensurePlayingUnmuted(v), 120);
        setTimeout(() => ensurePlayingUnmuted(v), 300);
        requestAnimationFrame(() => ensurePlayingUnmuted(v));
      });
      const FS_AUTO_RESUME_MS = 250;

      const isInFullscreen = (v: HTMLVideoElement) =>
        isFS || isFullscreen() || isElementFullscreen(v);

      const justEnteredFullscreen = () => Date.now() - lastFSEnterAt < FS_AUTO_RESUME_MS;

      v.addEventListener(
        'pause',
        () => {
          if (isInFullscreen(v) && justEnteredFullscreen()) {
            ensurePlayingUnmuted(v);
          }
        },
        true
      );

      v.addEventListener(
        'play',
        () => {
          if (!isInFullscreen(v)) return;
          try {
            v.muted = false;
            v.removeAttribute('muted');
            v.volume = Math.max(v.volume || 0, 0.8);
          } catch {
            /* empty */
          }
        },
        true
      );

      v.addEventListener('volumechange', () => {
        if (isInFullscreen(v) && justEnteredFullscreen() && v.muted) {
          ensurePlayingUnmuted(v);
        }
      });
      document.addEventListener('fullscreenchange', () => {
        if (isElementFullscreen(v) || isFullscreen()) {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(v);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
          setTimeout(() => ensurePlayingUnmuted(v), 120);
        } else {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(v);
          setTimeout(() => ensurePlayingUnmuted(v), 120);
          setTimeout(() => ensurePlayingUnmuted(v), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
        }
      });
      (document as any).addEventListener('webkitfullscreenchange', () => {
        if (isElementFullscreen(v) || isFullscreen()) {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(v);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
          setTimeout(() => ensurePlayingUnmuted(v), 120);
        } else {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(v);
          setTimeout(() => ensurePlayingUnmuted(v), 120);
          setTimeout(() => ensurePlayingUnmuted(v), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
        }
      });
      (v as any).addEventListener?.('webkitpresentationmodechanged', () => {
        if ((v as any).webkitPresentationMode === 'fullscreen') {
          setFullscreenMode(true);
          lastFSEnterAt = Date.now();
          ensurePlayingUnmuted(v);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
          setTimeout(() => ensurePlayingUnmuted(v), 120);
        }
        if ((v as any).webkitPresentationMode === 'inline') {
          setFullscreenMode(false);
          lastFSExitAt = Date.now();
          ensurePlayingUnmuted(v);
          setTimeout(() => ensurePlayingUnmuted(v), 120);
          setTimeout(() => ensurePlayingUnmuted(v), 300);
          requestAnimationFrame(() => ensurePlayingUnmuted(v));
        }
      });
    }

    Array.from(root.querySelectorAll<HTMLVideoElement>('video')).forEach(attachKeepPlaying);

    let previousCenterIndex = state.index;

    function updateStateDimensions() {
      const currentDims = getCurrentDims();
      state.startDims = { ...currentDims.startDims };
      state.midDims = { ...currentDims.midDims };
      state.endDims = { ...currentDims.endDims };

      if (isStatic && window.innerWidth > 700) {
        const SCALE_FACTOR = 1.2;
        state.endDims.centerW *= SCALE_FACTOR;
        state.endDims.centerH *= SCALE_FACTOR;
        state.midDims.centerW *= SCALE_FACTOR;
        state.midDims.centerH *= SCALE_FACTOR;
      }
    }

    updateStateDimensions();

    gsap.set(root, {
      css: {
        '--center-w': state.startDims.centerW,
        '--center-h': state.startDims.centerH,
        '--side-w': state.startDims.sideW,
        '--side-h': state.startDims.sideH,
        '--edge-gap': state.startDims.edgeGap,
        '--side-z': state.startDims.sideZ,
        '--carousel-bottom': state.startDims.bottom,
      },
    });

    gsap.set(cards, {
      xPercent: -50,
      yPercent: -50,
      left: '50%',
      top: '50%',
      position: 'absolute',
      opacity: 0,
      zIndex: 1,
      willChange: 'transform, opacity, width, height',
      transform: 'translate3d(0,0,0)',
    });

    function syncCardsListHeight(isMobile: boolean) {
      const cardsList = root.querySelector<HTMLElement>('.cards');
      if (!cardsList) return;

      if (!isMobile) {
        gsap.set(cardsList, { clearProps: 'height' });
        return;
      }

      const { height } = getMobileCardSize();
      gsap.set(cardsList, { height: Math.round(height) });
    }

    function layoutInstant() {
      if (isFS || isFullscreen()) return;

      const total = cards.length;
      const { centerW, centerH, sideW, sideH, edgeGap, sideZ } = state.dims;
      const sideOffset = centerW / 2 + sideW / 2 + edgeGap;
      const isMobile = window.innerWidth < 1000;

      if (gallery) {
        gsap.set(gallery, { height: cardsContainerHeight() });
      }
      syncCardsListHeight(isMobile);

      if (isMobile) {
        mobileScroll.position = state.index;
        drawMobileCards(state.index);
        return;
      }

      cards.forEach((el, i) => {
        const rel = (((i - state.index) % total) + total) % total;

        let x = 0;
        let w = sideW;
        let h = sideH;
        let z = -400;
        let opacity = 0;
        let zIndex = 1;

        if (rel === 0) {
          w = centerW;
          h = centerH;
          z = 0;
          opacity = 1;
          zIndex = 3;
        } else if (rel === 1) {
          x = sideOffset;
          z = sideZ;
          opacity = 1;
          zIndex = 2;
        } else if (rel === total - 1) {
          x = -sideOffset;
          z = sideZ;
          opacity = 1;
          zIndex = 2;
        } else {
          x = rel < total / 2 ? sideOffset * 2.5 : -sideOffset * 2.5;
        }

        gsap.set(el, { x, z, width: w, height: h, opacity, zIndex });
      });

      cards.forEach((el, i) => {
        const isActive = wrapIndex(i) === wrapIndex(state.index);
        el.classList.toggle('is-center', isActive);
        el.classList.toggle('is-left', false);
      });
    }

    function layoutAnimated() {
      if (isFS || isFullscreen()) return;

      const total = cards.length;
      const { centerW, centerH, sideW, sideH, edgeGap, sideZ } = state.dims;
      const sideOffset = centerW / 2 + sideW / 2 + edgeGap;
      const isMobile = window.innerWidth < 1000;
      syncCardsListHeight(isMobile);

      if (isMobile) {
        drawMobileCards(state.index);
        return;
      }

      const tl = gsap.timeline({
        defaults: { duration: 0.35, ease: 'power2.out' },
        onStart: () => {
          const currentCenterIndex = wrapIndex(state.index);
          const prevCenterIndex = wrapIndex(previousCenterIndex);

          if (currentCenterIndex !== prevCenterIndex) {
            const prevCard = cards[prevCenterIndex];
            const prevVideo = prevCard?.querySelector<HTMLVideoElement>('video');
            if (prevVideo) {
              requestAnimationFrame(() => {
                if (!(isFS || isFullscreen()) && !isElementFullscreen(prevVideo)) {
                  prevVideo.pause();
                  prevVideo.controls = false;
                  prevVideo.removeAttribute('controls');
                }
              });
            }

            const currentCard = cards[currentCenterIndex];
            const currentVideo = currentCard?.querySelector<HTMLVideoElement>('video');
            if (
              currentVideo &&
              (currentCard?.classList.contains('is-center') ||
                currentCard?.classList.contains('is-left')) &&
              !(isFS || isFullscreen())
            ) {
              try {
                currentVideo.play();
              } catch {
                /* empty */
              }
              currentVideo.setAttribute('controls', 'true');
            }
          }

          previousCenterIndex = state.index;
        },
      });

      cards.forEach((el, i) => {
        const rel = (((i - state.index) % total) + total) % total;

        let x = 0;
        let w = sideW;
        let h = sideH;
        let z = -400;
        let opacity = 0;
        let zIndex = 1;

        if (rel === 0) {
          w = centerW;
          h = centerH;
          z = 0;
          opacity = 1;
          zIndex = 3;
        } else if (rel === 1) {
          x = sideOffset;
          z = sideZ;
          opacity = 1;
          zIndex = 2;
        } else if (rel === total - 1) {
          x = -sideOffset;
          z = sideZ;
          opacity = 1;
          zIndex = 2;
        } else {
          x = rel < total / 2 ? sideOffset * 2.5 : -sideOffset * 2.5;
        }

        tl.to(el, { x, z, width: w, height: h, opacity, zIndex }, 0);
      });

      cards.forEach((el, i) => {
        const isActive = wrapIndex(i) === wrapIndex(state.index);
        el.classList.toggle('is-center', isActive);
        el.classList.toggle('is-left', false);
      });
    }

    const shouldShowDots = cards.length === 3;
    let dotsContainer: HTMLElement | null = null;
    const dotButtons: HTMLButtonElement[] = [];

    function updateDotsActive() {
      if (!dotsContainer || dotButtons.length !== 3) return;
      const active = wrapIndex(state.index);
      dotButtons.forEach((btn, i) => {
        const on = i === active;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }

    function goTo(index: number) {
      if (isMobileViewport()) {
        snapMobileTo(index);
        return;
      }
      state.index = wrapIndex(index);
      layoutAnimated();
      updateDotsActive();
    }

    function go(dir: number) {
      if (isMobileViewport()) {
        snapMobileTo(Math.round(mobileScroll.position) + dir);
        return;
      }
      state.index = wrapIndex(state.index + dir);
      layoutAnimated();
      updateDotsActive();
    }

    nextBtn?.addEventListener('click', () => go(1));
    prevBtn?.addEventListener('click', () => go(-1));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    });

    if (shouldShowDots && actions && !actions.querySelector('.dots') && prevBtn && nextBtn) {
      dotsContainer = document.createElement('div');
      dotsContainer.className = 'dots';
      dotsContainer.setAttribute('role', 'tablist');
      dotsContainer.setAttribute('aria-label', 'Carousel pagination');

      for (let i = 0; i < 3; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dot';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        b.addEventListener('click', () => goTo(i));
        dotsContainer.appendChild(b);
        dotButtons.push(b);
      }

      actions.insertBefore(dotsContainer, nextBtn);
      if (isAnimationEnabled()) {
        gsap.set(dotsContainer, { opacity: 0, y: 60, pointerEvents: 'none' });
      } else {
        gsap.set(dotsContainer, { opacity: 1, y: 0, pointerEvents: 'auto' });
      }
    }

    layoutInstant();
    updateDotsActive();
    runStartupAnimation();

    if (isAEMAuthorMode()) {
      ScrollTrigger.killAll();
    }

    const cardsElement = root.querySelector('.cards') as Element;

    // IMPORTANTE: Assicurati che wheel events passino attraverso
    if (cardsElement) {
      cardsElement.addEventListener(
        'wheel',
        (e) => {
          // Lascia passare lo scroll verticale
          if (Math.abs((e as WheelEvent).deltaY) > Math.abs((e as WheelEvent).deltaX)) {
            return;
          }
        },
        { passive: true }
      );
    }

    const MOBILE_MIN_SWIPE_PX = 40;

    const dragInstance = Draggable.create(proxy, {
      type: 'x',
      trigger: cardsElement,
      dragClickables: false,
      allowNativeTouchScrolling: true,
      onPress() {
        (this as any)._startX = this['x'];
        (this as any)._startPosition = mobileScroll.position;
        mobileSnap?.kill();
      },
      onDrag() {
        if (isFS || isFullscreen()) return;
        const { step } = mobileTrack();
        const dragX = this['x'] - (this as any)._startX;
        mobileScroll.position = (this as any)._startPosition - dragX / step;
        drawMobileCards(mobileScroll.position);
      },
      onRelease() {
        if (isFS || isFullscreen()) return;

        const dragX = this['x'] - (this as any)._startX;
        const startIndex = Math.round((this as any)._startPosition);

        // Snap to the nearest card, but let a short, deliberate flick still
        // advance one card even when it doesn't cross the half-card line.
        let target = Math.round(mobileScroll.position);
        if (target === startIndex && Math.abs(dragX) > MOBILE_MIN_SWIPE_PX) {
          target = startIndex + (dragX < 0 ? 1 : -1);
        }

        gsap.set(this['target'], { x: 0 });
        snapMobileTo(target);
      },
    })[0];

    DRAGGABLES.push(dragInstance);

    dragInstance.disable();

    let wasInteractive = false;
    function setInteractive(on: boolean) {
      if (on === wasInteractive) return;
      wasInteractive = on;
      root.classList.toggle('is-interactive', on);

      if (on && window.innerWidth <= 1000) {
        dragInstance.enable();
      } else {
        dragInstance.disable();
      }
    }

    const titleEl =
      root
        .closest('.cmp-parallax')
        ?.querySelector<HTMLElement>('.cmp-parallax__title .cmp-title') || null;

    const paraMainEl =
      root.closest('.cmp-parallax')?.querySelector<HTMLElement>('.cmp-parallax__paragraph-main') ||
      null;

    const paraSecondaryEl =
      root
        .closest('.cmp-parallax')
        ?.querySelector<HTMLElement>('.cmp-parallax__paragraph-secondary') || null;

    const bgEl =
      root.closest('.cmp-parallax')?.querySelector<HTMLElement>('.cmp-parallax__background') ||
      null;

    function positionTitleAboveCard(onComplete?: () => void): void {
      if (!titleEl) {
        if (onComplete) onComplete();
        return;
      }

      const centerCard = cards[wrapIndex(state.index)];
      if (!centerCard) {
        if (onComplete) onComplete();
        return;
      }

      if (isStatic) {
        gsap.delayedCall(0, () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const cardRect = centerCard.getBoundingClientRect();
              const titleRect = titleEl.getBoundingClientRect();

              const currentTitleBottom = titleRect.bottom;
              const targetTitleBottom = cardRect.top - TITLE_CARD_GAP_PX;
              const deltaY = targetTitleBottom - currentTitleBottom;

              const currentY = (gsap.getProperty(titleEl, 'y') as number) || 0;
              const newY = currentY + deltaY;

              gsap.set(titleEl, {
                y: newY,
                xPercent: -50,
                opacity: 1,
                force3D: true,
                willChange: 'transform, opacity',
              });

              if (onComplete) {
                requestAnimationFrame(onComplete);
              }
            });
          });
        });
      } else {
        // Versione per carosello SCROLLABILE
        applyDims(state.startDims);
        layoutInstant();

        centerCard.offsetHeight;
        titleEl.offsetHeight;

        const cardRect = centerCard.getBoundingClientRect();
        const titleRect = titleEl.getBoundingClientRect();

        const bp = getCurrentBreakpoint();
        let newY: number;

        if (bp === 'md') {
          const currentTitleBottom = titleRect.bottom;
          const targetTitleBottom = cardRect.top - TITLE_CARD_GAP_PX;
          const deltaY = targetTitleBottom - currentTitleBottom;
          const currentY = (gsap.getProperty(titleEl, 'y') as number) || 0;
          newY = currentY + deltaY + 150;
        } else {
          const currentTitleBottom = titleRect.bottom;
          const targetTitleBottom = cardRect.top - TITLE_CARD_GAP_PX + 150;
          const deltaY = targetTitleBottom - currentTitleBottom;
          const currentY = (gsap.getProperty(titleEl, 'y') as number) || 0;
          newY = currentY + deltaY;
        }

        gsap.set(titleEl, {
          y: newY,
          xPercent: -50,
          opacity: 1,
          force3D: true,
          willChange: 'transform, opacity',
        });

        if (onComplete) {
          onComplete();
        }
      }
    }

    function placeParagraphs() {
      if (!gallery) return;

      if (!isAnimationEnabled()) return;

      if (paraSecondaryEl) {
        if (!gallery.contains(paraSecondaryEl)) gallery.appendChild(paraSecondaryEl);

        gsap.set(paraSecondaryEl, {
          position: 'absolute',
          left: '50%',
          xPercent: -50,
          zIndex: 11,
          pointerEvents: 'none',
          willChange: 'transform,opacity',
          y: 60,
          opacity: 0,
          force3D: true,
        });
      }

      if (paraMainEl) {
        if (!gallery.contains(paraMainEl)) gallery.appendChild(paraMainEl);

        const bottomMainPx = cardsContainerHeight() + MAIN_ABOVE_GAP_PX;

        gsap.set(paraMainEl, {
          position: 'absolute',
          left: '50%',
          xPercent: -50,
          bottom: bottomMainPx,
          zIndex: 11,
          pointerEvents: 'none',
          willChange: 'transform,opacity',
          y: 60,
          opacity: 0,
          force3D: true,
        });
      }
    }

    let titleST: ScrollTrigger | null = null;
    let savedCarouselYStart: number | null = null;
    let carouselST: ScrollTrigger;

    function buildTabletTitleAnimation(getPinnedRange: () => number) {
      if (!titleEl || !isAnimationEnabled()) {
        return;
      }

      const bp = getCurrentBreakpoint();
      if (bp !== 'md') {
        return;
      }

      // Posiziona il titolo prima
      positionTitleAboveCard(() => {
        savedCarouselYStart = (gsap.getProperty(titleEl, 'y') as number) || 0;

        const TITLE_SPEED = 1.5;
        const totalMove = carouselST.end * TITLE_SPEED;

        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: () => `+=${carouselST.end}`,
          scrub: 0.3,
          onUpdate: (self) => {
            if (isFS || isFullscreen()) return;
            if (savedCarouselYStart === null) return;

            const titleY = savedCarouselYStart - self.progress * totalMove;
            const titleOpacity =
              self.progress > 0.2 ? Math.max(0, 1 - (self.progress - 0.2) / 0.3) : 1;

            gsap.set(titleEl, {
              y: titleY,
              opacity: titleOpacity,
              xPercent: -50,
            });
          },
        });
      });
    }

    function buildTitleAnimations(getPinnedRange: () => number) {
      if (!titleEl || !isAnimationEnabled()) {
        return;
      }

      if (titleST) {
        return;
      }

      const bp = getCurrentBreakpoint();

      if (bp === 'md') {
        return;
      }

      const totalScrollRange = getPinnedRange();
      const TITLE_SPEED = 1.2;
      const totalMove = totalScrollRange * TITLE_SPEED;

      positionTitleAboveCard(() => {
        savedCarouselYStart = (gsap.getProperty(titleEl, 'y') as number) || 0;
        const yStart = savedCarouselYStart;
        const yEnd = yStart - totalMove;

        const tl = gsap.timeline({ paused: true });

        tl.fromTo(
          titleEl,
          { y: yStart, opacity: 1 },
          {
            y: yEnd,
            opacity: 1,
            ease: 'power3.out',
            duration: 1,
          }
        );

        tl.to(
          titleEl,
          {
            opacity: 0,
            ease: 'none',
            duration: 0.4,
          },
          0.3
        );

        titleST = ScrollTrigger.create({
          trigger: root,
          start: () => `top-=${HEADER_OFFSET_PX} top`,
          end: () => `+=${totalScrollRange}`,
          scrub: 0.3,
          animation: tl,
          invalidateOnRefresh: false,
          onRefresh: () => {
            if (titleST && savedCarouselYStart !== null) {
              const scrollTop =
                window.pageYOffset ||
                document.documentElement.scrollTop ||
                document.body.scrollTop ||
                0;
              if (scrollTop < 50) {
                gsap.set(titleEl, {
                  y: savedCarouselYStart,
                  opacity: 1,
                  xPercent: -50,
                });
              }
            }
          },
        });

        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (scrollTop < 50) {
          gsap.set(titleEl, {
            y: savedCarouselYStart,
            opacity: 1,
            xPercent: -50,
          });
        }
      });
    }

    function updateByProgress(p: number) {
      if (isFS || isFullscreen()) return;

      const COMPRESS_END = 0.5;
      const effectiveProgress = clamp01(p / COMPRESS_END);

      if (!isAnimationEnabled()) {
        applyDims(state.endDims);
        layoutInstant();
        return;
      }

      const split = CAROUSEL_SPLIT;
      const centerBottom = bottomForCenter();

      if (effectiveProgress < split) {
        const t = effectiveProgress / split;
        const d = {
          centerW: lerp(state.startDims.centerW, state.midDims.centerW, t),
          centerH: lerp(state.startDims.centerH, state.midDims.centerH, t),
          sideW: lerp(state.startDims.sideW, state.midDims.sideW, t),
          sideH: lerp(state.startDims.sideH, state.midDims.sideH, t),
          edgeGap: lerp(state.startDims.edgeGap, state.midDims.edgeGap, t),
          sideZ: state.startDims.sideZ,
          bottom: lerp(state.startDims.bottom, centerBottom, t),
        };
        applyDims(d);
      } else {
        const t = (effectiveProgress - split) / (1 - split);
        const d = {
          centerW: lerp(state.midDims.centerW, state.endDims.centerW, t),
          centerH: lerp(state.midDims.centerH, state.endDims.centerH, t),
          sideW: lerp(state.midDims.sideW, state.endDims.sideW, t),
          sideH: lerp(state.midDims.sideH, state.endDims.sideH, t),
          edgeGap: lerp(state.midDims.edgeGap, state.endDims.edgeGap, t),
          sideZ: state.midDims.sideZ,
          bottom: centerBottom,
        };
        applyDims(d);
      }

      if (actions) {
        const fade = clamp01((effectiveProgress - 0.85) / 0.15);
        gsap.set(actions, { opacity: fade });
      }

      const t = clamp01((effectiveProgress - 0.1) / 0.3);
      if (paraSecondaryEl) gsap.set(paraSecondaryEl, { opacity: 0 + t * 0.8, y: (1 - t) * 60 });
      if (paraMainEl) {
        const finalY = isStatic ? -40 : 0;
        gsap.set(paraMainEl, { opacity: 0 + t * 0.8, y: lerp(60, finalY, t) });
      }
      if (prevBtn)
        gsap.set(prevBtn, {
          opacity: t,
          y: (1 - t) * 60,
          pointerEvents: t > 0.01 ? 'auto' : 'none',
        });
      if (nextBtn)
        gsap.set(nextBtn, {
          opacity: t,
          y: (1 - t) * 60,
          pointerEvents: t > 0.01 ? 'auto' : 'none',
        });
      if (dotsContainer) {
        gsap.set(dotsContainer, {
          opacity: t,
          y: (1 - t) * 60,
          pointerEvents: t > 0.01 ? 'none' : 'none',
        });
      }

      if (bgEl) {
        const ov = clamp01(effectiveProgress);
        gsap.set(bgEl, { '--bg-overlay-op': ov });
      }

      setInteractive(effectiveProgress >= 0.85);
      layoutInstant();
    }

    let carouselLeaveCenterIntervalId: number | null = null;
    let carouselWasPushedDown = false;

    const syncCarouselLeaveCenterState = () => {
      if (!gallery || !root.isConnected) {
        stopCarouselLeaveCenterInterval();
        return;
      }
      if (isFS || isFullscreen()) return;

      const triggerRect = root.getBoundingClientRect();
      const triggerTop = triggerRect.top + window.pageYOffset;
      const carouselEndAbsolute = triggerTop + headerHeight + endPosition;
      const currentScroll = window.pageYOffset;

      if (currentScroll >= carouselEndAbsolute - scrollPositionTrigger + intervalDistanceOffset) {
        if (!carouselWasPushedDown) {
          gallery.classList.add('leave-center');
          carouselWasPushedDown = true;
        }
      } else if (carouselWasPushedDown) {
        gallery.classList.remove('leave-center');
        carouselWasPushedDown = false;
      }
    };

    const startCarouselLeaveCenterInterval = () => {
      if (!gallery || !isAnimationEnabled()) return;
      if (carouselLeaveCenterIntervalId !== null) return;
      carouselLeaveCenterIntervalId = window.setInterval(
        syncCarouselLeaveCenterState,
        intervalDuration
      );
      syncCarouselLeaveCenterState();
    };

    const stopCarouselLeaveCenterInterval = () => {
      if (carouselLeaveCenterIntervalId === null) return;
      window.clearInterval(carouselLeaveCenterIntervalId);
      carouselLeaveCenterIntervalId = null;
    };

    carouselST = ScrollTrigger.create({
      trigger: root,
      start: () => {
        const vh = window.innerHeight;
        const galleryEl = root.querySelector('.gallery') as HTMLElement;
        const realGalleryH = galleryEl?.offsetHeight || cardsContainerHeight();

        const rootRect = root.getBoundingClientRect();
        const galleryRect = galleryEl?.getBoundingClientRect();
        const relativeOffset = (galleryRect?.top || 0) - rootRect.top;

        const desiredGalleryTop = (vh - realGalleryH) / 2;
        let dynamicNudge = 40;

        if (vh < 700) {
          dynamicNudge -= (700 - vh) * 0.2;
        } else {
          dynamicNudge += (vh - 700) * 0.25;
        }

        const ww = window.innerWidth;
        const wh = window.innerHeight;
        let staticFactor = 0;

        if (ww >= 3800) {
          if (wh >= 2000 && wh <= 2500) staticFactor = 340;
        } else if (ww >= 1920) {
          if (wh > 2000 && wh <= 2500) staticFactor = 300;
          else if (wh > 1300 && wh <= 1700) staticFactor = 160;
          else if (wh > 1200 && wh <= 1300) staticFactor = 60;
          else if (wh > 1100 && wh <= 1200) staticFactor = 95;
          else if (wh > 1000 && wh <= 1100) staticFactor = 90;
          else if (wh > 900 && wh <= 1000) staticFactor = 120;
          else if (wh > 700 && wh <= 900) staticFactor = 130;
        } else if (ww >= 1680 && wh > 700 && wh <= 1300) {
          staticFactor = 30;
        } else if (ww >= 1440 && wh > 700 && wh <= 1300) {
          staticFactor = 40;
        }
        const startPosition = desiredGalleryTop - relativeOffset + dynamicNudge - staticFactor;

        return `top top+=${startPosition}`;
      },
      end: () => `+=${endPosition}`,
      pin: isAnimationEnabled() ? gallery || root : false,
      pinType: 'fixed',
      pinSpacing: isAnimationEnabled(),
      scrub: isAnimationEnabled(),
      anticipatePin: 0.5,
      fastScrollEnd: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (isFS || isFullscreen()) return;
        const video = getCenterVideo();
        if (!isAnimationEnabled()) return;
        const animationProgress = self.progress;
        const v = (self as any).getVelocity ? (self as any).getVelocity() : 0;

        if (
          Date.now() - lastFSExitAt > RECENT_EXIT_MS &&
          self.direction == -1 &&
          animationProgress <= 0.3
        ) {
          (document.querySelector('.cmp-text__button--less') as HTMLButtonElement)?.click();

          const bgEl = root
            .closest('.cmp-parallax')
            ?.querySelector<HTMLElement>('.cmp-parallax__background');
          if (bgEl) {
            gsap.to(bgEl, {
              '--bg-overlay-op': animationProgress,
              duration: 0.4,
              ease: 'power2.out',
            });
          }

          if (video) {
            requestAnimationFrame(() => {
              if (!isElementFullscreen(video) && !(isFS || isFullscreen())) {
                video.pause();
                video.currentTime = 0;
              }
            });
          }
        }

        smoothUpdate(self.progress);

        if (animationProgress >= 0.5 && self.direction == 1) {
          if (video && !(isFS || isFullscreen())) {
            try {
              video.play();
            } catch {
              /* empty */
            }
          }
        }
      },
      onEnter: () => {
        startCarouselLeaveCenterInterval();
      },
      onLeave: () => {
        stopCarouselLeaveCenterInterval();
        gallery?.classList.add('leave-center');
      },
      onEnterBack: () => {
        gallery?.classList.remove('leave-center');
        carouselWasPushedDown = false;
        startCarouselLeaveCenterInterval();
        requestAnimationFrame(() => {
          gallery?.classList.remove('leave-center');
          window.scrollBy({ top: -10, behavior: 'auto' });
        });
      },
      onLeaveBack: () => {
        stopCarouselLeaveCenterInterval();
        gallery?.classList.remove('leave-center');
        carouselWasPushedDown = false;
      },
    });

    if (gallery && isAnimationEnabled() && carouselST.isActive) {
      startCarouselLeaveCenterInterval();
    }

    const getPinnedRange = () => carouselST.end - carouselST.start;

    ScrollTrigger.addEventListener('refreshInit', () => {
      updateStateDimensions();
      state.midDims.bottom = bottomForCenter();
      state.endDims.bottom = bottomForCenter();
      if (gallery) {
        gsap.set(gallery, { height: cardsContainerHeight() });
      }
    });

    ScrollTrigger.addEventListener('refresh', () => {
      placeParagraphs();
      if (bgEl) gsap.set(bgEl, { '--bg-overlay-op': 0 });
    });

    layoutInstant();
    if (!isAnimationEnabled()) {
      applyDims(state.endDims);
      layoutInstant();

      if (actions) {
        gsap.set(actions, { opacity: 1 });
      }
      if (prevBtn) {
        gsap.set(prevBtn, { opacity: 1, y: 0, pointerEvents: 'auto' });
      }
      if (nextBtn) {
        gsap.set(nextBtn, { opacity: 1, y: 0, pointerEvents: 'auto' });
      }
      if (dotsContainer) {
        gsap.set(dotsContainer, { opacity: 1, y: 0, pointerEvents: 'auto' });
      }

      if (window.innerWidth < 1000 || !isStatic) dragInstance.enable();
      root.classList.add('is-interactive');

      return;
    }
    if (isAnimationEnabled()) {
      buildTitleAnimations(getPinnedRange);
      buildTabletTitleAnimation(getPinnedRange);
    }
    placeParagraphs();
    if (bgEl) gsap.set(bgEl, { '--bg-overlay-op': 0 });

    installMediaObserver(root.closest('.cmp-parallax') || root);

    if (gallery) {
      installScrollActivityClass(gallery, 'gallery-is-scrolling', 500);
    }

    const cleanupCarouselResize = subscribeToResize(() => {
      if (!root.isConnected) {
        cleanupCarouselResize();
        return;
      }
      if (isFS || isFullscreen()) return;
      updateStateDimensions();
      applyDims(state.startDims);
      if (gallery) {
        gsap.set(gallery, { height: cardsContainerHeight() });
      }
      placeParagraphs();
      if (isAnimationEnabled()) {
        ScrollTrigger.refresh();
      } else {
        drawMobileCards(state.index);
      }
    });
  });
}

function initReadMoreFade(): void {
  const expandables = document.querySelectorAll<HTMLElement>(
    '.cmp-parallax__paragraph-secondary.cmp-text__expandable, .cmp-parallax__paragraph-main.cmp-text__expandable'
  );

  expandables.forEach((container) => {
    const moreBtn = container.querySelector<HTMLElement>('.cmp-text__button--more');
    const lessBtn = container.querySelector<HTMLElement>('.cmp-text__button--less');
    const hiddenTexts = container.querySelectorAll<HTMLElement>('.cmp-text__hidden-text');

    const parallaxRoot = container.closest('.cmp-parallax');
    const bgEl = parallaxRoot?.querySelector<HTMLElement>('.cmp-parallax__background');

    let savedMobileScroll = -1;

    if (!moreBtn || !lessBtn || hiddenTexts.length === 0) return;

    moreBtn.addEventListener('click', () => {
      if (window.innerWidth < 1000) {
        savedMobileScroll =
          window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      }

      container.classList.add('-expanded-text');

      if (bgEl) {
        gsap.to(bgEl, {
          '--bg-overlay-op': 1,
          duration: 0.4,
          ease: 'power2.out',
        });
      }

      requestAnimationFrame(() => {
        gsap.fromTo(
          hiddenTexts,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            stagger: 0.15,
          }
        );
      });
    });

    lessBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (window.innerWidth < 1000 && savedMobileScroll !== -1) {
        document.body.scrollTo({ top: savedMobileScroll, behavior: 'smooth' });
        document.documentElement.scrollTo({ top: savedMobileScroll, behavior: 'smooth' });
      }

      gsap.to(hiddenTexts, {
        opacity: 0,
        y: -20,
        duration: 0.2,
        ease: 'power2.in',
        stagger: 0.05,
        onComplete: () => {
          container.classList.remove('-expanded-text');
        },
      });
    });
  });
}

function initInPageAnchorScroll(): void {
  const parallaxRoot = document.querySelector<HTMLElement>('.cmp-parallax');
  if (!parallaxRoot) return;

  parallaxRoot.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href^="#"]');
    if (!link) return;

    const id = decodeURIComponent((link.getAttribute('href') || '').slice(1));
    const target = document.getElementById(id);
    if (!id || !target) return;

    e.preventDefault();
    history.pushState(null, '', `#${id}`);

    if (!isAnimationEnabled()) {
      const scrollMarginTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const top = getPageScrollY() + target.getBoundingClientRect().top - scrollMarginTop;
      document.body.scrollTo({ top, behavior: 'smooth' });
      document.documentElement.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    setParallaxAutoSnapSuppressed(true);
    document.body.scrollTo({ top: getParallaxRestScrollY(), behavior: 'smooth' });
    window.setTimeout(() => setParallaxAutoSnapSuppressed(false), 1000);
  });
}

function onDocumentReady(): void {
  if (!document.body.querySelector('.cmp-parallax')) return;

  initReadMoreFade();
  if (window.innerWidth < 1000) {
    updateMobileTitleWrapState();
    setPositionTitleMobile();
    document.querySelector('.cmp-parallax__title--mobile')?.classList.add('is-ready');
    observeMobileTitleForChanges();
  }

  if (document.querySelector('.cmp-parallax-carousel')) {
    initParallaxCarousel();
  }
  if (document.querySelector('.cmp-single-item') && isAnimationEnabled()) {
    initParallaxSingleItem();
  }
  initTopCtaButton();
  initInPageAnchorScroll();

  // Pause parallax videos when scrolled out of view; resume when scrolled back in.
  // This is intentionally independent from ScrollTrigger progress, and respects fullscreen.
  const parallaxRoot = document.body.querySelector('.cmp-parallax');
  if (parallaxRoot) {
    installParallaxVideoInViewBehavior(parallaxRoot);
  }

  window.addEventListener('scroll', () => {
    scrollListener();
    try {
      ScrollTrigger.update();
    } catch {
      /* empty */
    }
  });
  if (window.innerWidth >= 1000) observeDynamicTabs();
  const title = document.querySelector<HTMLElement>(
    '.cmp-parallax .cmp-parallax__title .cmp-title'
  );
  const prev = document.querySelector<HTMLElement>('.cmp-parallax .prev');
  title?.addEventListener('keydown', (e: KeyboardEvent) => {
    const event = e as KeyboardEvent;
    if (event.key === 'Tab' && !event.shiftKey) {
      e.preventDefault();
      getScrollElement().scrollTo({
        top: window.innerHeight + headerHeight,
        behavior: 'smooth',
      });
      setTimeout(() => prev?.focus(), 500);
    }
  });
  prev?.addEventListener('keydown', (e: KeyboardEvent) => {
    const event = e as KeyboardEvent;
    if (event.key === 'Tab' && event.shiftKey) {
      e.preventDefault();
      getScrollElement().scrollTo({
        top: headerHeight,
        behavior: 'smooth',
      });
      setTimeout(() => title?.focus(), 500);
    }
  });

  const scrollKeys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar'];
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    handleArrowKeySnap(e);
    if (!scrollKeys.includes(e.key)) return;
    requestAnimationFrame(() => {
      try {
        ScrollTrigger.update();
      } catch {
        // best-effort cleanup; safe to ignore failures here
      }
    });
  });
}

function remeasureMobileTitleWhenBgReady() {
  if (window.innerWidth >= 1000) return;
  const bgImg = document.querySelector<HTMLImageElement>('.cmp-parallax__background img');
  if (!bgImg) return;
  const run = () => {
    updateMobileTitleWrapState();
    setPositionTitleMobile();
    document.querySelector('.cmp-parallax__title--mobile')?.classList.add('is-ready');
  };
  if (bgImg.complete && bgImg.naturalWidth) run();
  else {
    bgImg.addEventListener('load', run, { once: true });
    bgImg.addEventListener('error', run, { once: true });
  }
}

function initAuthorParallaxStatic(): void {
  document.querySelectorAll<HTMLElement>('.cmp-parallax-carousel .cards').forEach((el) => {
    el.classList.add('is-ready');
  });
  document.querySelector('.cmp-parallax__title--mobile')?.classList.add('is-ready');
  document.querySelectorAll<HTMLVideoElement>('.cmp-parallax video').forEach((video) => {
    video.pause();
  });
}

function landOnHashTarget(): void {
  const id = decodeURIComponent((window.location.hash || '').slice(1));
  const target = id ? document.getElementById(id) : null;
  if (!target) return;

  const SETTLE_MS = 400;
  const MAX_MS = 5000;
  const ALIGN_TOLERANCE_PX = 2;

  let finished = false;
  let rafId = 0;
  let alignedSince = 0;

  const finish = () => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener('wheel', finish);
    window.removeEventListener('touchstart', finish);
    window.removeEventListener('keydown', finish);
    setParallaxAutoSnapSuppressed(false);
  };

  const reconcile = (now: number, startedAt: number) => {
    if (finished) return;

    if (Math.abs(target.getBoundingClientRect().top) > ALIGN_TOLERANCE_PX) {
      target.scrollIntoView();
      alignedSince = 0; // drifted again; restart the settle window
    } else if (!alignedSince) {
      alignedSince = now;
    }

    const settled = alignedSince !== 0 && now - alignedSince >= SETTLE_MS;
    if (settled || now - startedAt >= MAX_MS) {
      finish();
      return;
    }
    rafId = requestAnimationFrame((t) => reconcile(t, startedAt));
  };

  window.addEventListener('wheel', finish, { passive: true });
  window.addEventListener('touchstart', finish, { passive: true });
  window.addEventListener('keydown', finish);

  setParallaxAutoSnapSuppressed(true);
  rafId = requestAnimationFrame((t) => reconcile(t, t));
}

async function bootParallax(): Promise<void> {
  if (!document.body.querySelector('.cmp-parallax')) return;
  if (isAEMAuthorMode()) {
    initAuthorParallaxStatic();
    return;
  }

  lockScroll(true);
  try {
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    await waitForCriticalParallaxAssets();
    remeasureMobileTitleWhenBgReady();
    onDocumentReady();
    document.querySelectorAll<HTMLElement>('.cmp-parallax-carousel .cards').forEach((el) => {
      setTimeout(() => {
        const before = el.getBoundingClientRect().height;
        el.classList.add('is-ready');
        const after = el.getBoundingClientRect().height;
        if (Math.abs(after - before) > 0.5) {
          scheduleScrollTriggerRefresh();
        }
      }, 200);
    });
    ScrollTrigger.refresh();
    const postRefreshSignature = getParallaxLayoutSignature();
    await new Promise((r) => setTimeout(r, PARALLAX_REFRESH_SETTLE_MS));
    if (getParallaxLayoutSignature() !== postRefreshSignature) {
      ScrollTrigger.refresh();
    }
  } finally {
    lockScroll(false);
    if (window.location.hash) {
      landOnHashTarget();
    }
  }

  document.body.addEventListener('scroll', () => ScrollTrigger.update(), { passive: true });
}

let didBootParallax = false;
function startBootParallax() {
  if (didBootParallax) return;
  didBootParallax = true;
  void bootParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBootParallax, { once: true });
  window.addEventListener('load', startBootParallax, { once: true });
} else {
  startBootParallax();
}
